/**
 * Shared utilities for API client modules.
 */

import { codeToI18nKey, hasMappedErrorCode } from './errorCodes';

export type ApiErrorParams = Record<string, string | number | boolean | null>;

export type ApiError = {
  /** Stable snake_case code the client maps to an i18n key. */
  code: string;
  /** Interpolation values for the i18n string, e.g. {provider: 'google'}. */
  params?: ApiErrorParams;
  /** Human-readable English fallback — present when a code has no i18n mapping. */
  message?: string;
  /** HTTP status code. */
  status: number;
};

/**
 * Parse a non-OK response into a structured ApiError.
 *
 * The Go backend's `writeErrorCode` helper emits
 * `{success: false, error: code, code, params, message}`. Legacy sites that
 * still call `writeError` emit `{success: false, error: string, message}` —
 * in that case `code` falls back to the string in `error` (normalized).
 *
 * The returned `code` is always populated so callers can unconditionally look
 * it up in the central `codeToI18nKey` map.
 */
export async function extractApiError(res: Response): Promise<ApiError> {
  let parsed: Record<string, unknown> | null = null;
  try {
    const text = await res.text();
    if (text) parsed = JSON.parse(text) as Record<string, unknown>;
  } catch {
    parsed = null;
  }

  const raw = parsed ?? {};
  const codeField = typeof raw.code === 'string' ? raw.code : undefined;
  const errorField = typeof raw.error === 'string' ? raw.error : undefined;
  const messageField = typeof raw.message === 'string' ? raw.message : undefined;
  const paramsField =
    raw.params && typeof raw.params === 'object' && !Array.isArray(raw.params)
      ? (raw.params as ApiErrorParams)
      : undefined;

  const code = codeField ?? errorField ?? `http_${res.status}`;

  return {
    code,
    params: paramsField,
    message: messageField ?? errorField ?? res.statusText ?? undefined,
    status: res.status,
  };
}

/**
 * @deprecated Use extractApiError for structured handling. This helper is kept
 * for call sites that only display a raw English string — new code should map
 * the code to an i18n key via codeToI18nKey().
 */
export async function extractErrorText(res: Response): Promise<string> {
  const err = await extractApiError(res);
  return err.message ?? err.code;
}

/**
 * Callable shape of next-intl's `useTranslations()` / `getTranslations()`.
 *
 * next-intl's values are narrower (`string | number | Date`) than the
 * ApiError params (`string | number | boolean | null`) — translateApiError
 * coerces the incoming params before invoking this function.
 */
export type TranslateFn = (key: string, values?: Record<string, string | number | Date>) => string;

/**
 * Translate an ApiError to a user-facing string.
 *
 * Resolution order:
 *   1. If the code maps to a known i18n key, use it with the error's params.
 *   2. Fall back to the server-supplied English `message` if present.
 *   3. Fall back to the generic error translation.
 *
 * This matches the AuthModal pattern but keeps the logic in one shared place so
 * individual call sites don't re-implement the fallback ladder.
 */
export function translateApiError(err: Pick<ApiError, 'code' | 'params' | 'message'>, t: TranslateFn): string {
  if (err.code && hasMappedErrorCode(err.code)) {
    const key = codeToI18nKey(err.code);
    const values = coerceApiErrorParams(err.params);
    return t(key, values);
  }
  if (err.message && err.message.trim()) {
    return err.message.trim();
  }
  return t('errors.api.generic');
}

// coerceApiErrorParams narrows ApiErrorParams (string | number | boolean | null)
// down to the (string | number | Date) shape next-intl expects. Booleans are
// coerced to "true"/"false", nulls to empty strings — the server is responsible
// for not sending either when the i18n string cares about the literal type.
function coerceApiErrorParams(params: ApiErrorParams | undefined): Record<string, string | number | Date> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | Date> = {};
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string' || typeof v === 'number') out[k] = v;
    else if (typeof v === 'boolean') out[k] = v ? 'true' : 'false';
    else if (v === null) out[k] = '';
  }
  return out;
}

/**
 * Type-safe unwrap for responses from the Go backend's `writeSuccess` helper,
 * which wraps payloads in `{ success: true, data: {...} }`.
 *
 * Falls back to treating the raw body as `T` if the envelope markers are not
 * present — this keeps the helper usable across both standard and legacy
 * endpoints.
 */
export async function unwrap<T>(res: Response): Promise<T> {
  const body: unknown = await res.json();
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) {
    return (body as { data: T }).data;
  }
  return body as T;
}
